import CoreContracts = require("TFS/Core/Contracts");
import * as WorkContracts from "TFS/Work/Contracts";
import * as WorkClient from "TFS/Work/RestClient";
import { WorkItemTypeColorAndIconsProvider, IColorAndIcon } from "Presentation/Scripts/TFS/FeatureRef/WorkItemTypeColorAndIconsProvider";
import { ProcessesQuery, ProcessesEntry } from "Analytics/Scripts/QueryCache/ProcessesQuery";
import { SettingsHelper } from "Widgets/Scripts/Utilities/SettingsHelper";

export interface BacklogInformation {
    /** Expresses Work Item Types associated with the backlog*/
    workItemTypes: string[];

    /** Expresses Work Item Types associated with the backlog union'ed with bug types.*/
    bugsAndWorkItemTypes: string[];
}

/**
 * A utility class to help load process configuration
 */
export class KanbanTimeAgileSettingsHelper {
    public static getBacklogInformation(projectId: string, backlogCategory: string): IPromise<BacklogInformation> {
        return SettingsHelper.runQueryOnDashboardCache(ProcessesQuery.onProject(projectId)).then(processEntries => {
            const result: BacklogInformation = {
                workItemTypes: [],
                bugsAndWorkItemTypes: []
            };

            for (let i = 0; i < processEntries.length; i++) {
                const entry = processEntries[i];
                if (entry.BacklogCategoryReferenceName === backlogCategory) {
                    if (result.workItemTypes.indexOf(entry.WorkItemType) < 0) {
                        result.workItemTypes.push(entry.WorkItemType);
                    }
                    if (result.bugsAndWorkItemTypes.indexOf(entry.WorkItemType) < 0) {
                        result.bugsAndWorkItemTypes.push(entry.WorkItemType);
                    }
                } else if (entry.IsBugType) {
                    if (result.bugsAndWorkItemTypes.indexOf(entry.WorkItemType) < 0) {
                        result.bugsAndWorkItemTypes.push(entry.WorkItemType);
                    }
                }
            }
            return result;
        });
    }

    public static getTeamBugsBehavior(project: string, team: string): IPromise<WorkContracts.BugsBehavior> {
        const teamContext = {
            project: project,
            team: team
        } as CoreContracts.TeamContext;

        const client = WorkClient.getClient();
        return client.getTeamSettings(teamContext).then(settings => {
            return settings.bugsBehavior;
        });
    }

    public static getWorkItemTypes(projectId, filterBacklogTypes?: boolean): IPromise<string[]> {
        return SettingsHelper.runQueryOnDashboardCache(ProcessesQuery.onProject(projectId)).then(processEntries => {
            const workItemTypes: string[] = [];
            for (let i = 0; i < processEntries.length; i++) {
                const entry = processEntries[i];
                if (filterBacklogTypes) {
                    if (entry.IsBugType || entry.HasBacklog) {
                        KanbanTimeAgileSettingsHelper.checkAndInsertWorkItemType(workItemTypes, entry);
                    }
                } else {
                    KanbanTimeAgileSettingsHelper.checkAndInsertWorkItemType(workItemTypes, entry);
                }
            }
            return workItemTypes;
        });
    }

    public static getWITColorAndIconDictionary(projectContext: ContextIdentifier): IPromise<IDictionaryStringTo<IColorAndIcon>> {
        return KanbanTimeAgileSettingsHelper.getWorkItemTypes(projectContext.id).then(workItemTypeNames => {
            const projectName = projectContext.name;
            return WorkItemTypeColorAndIconsProvider.getInstance()
                .ensureColorAndIconsArePopulated([projectName])
                .then(() => {
                    const colorDictionary: IDictionaryStringTo<IColorAndIcon> = {};
                    workItemTypeNames.forEach(workItemTypeName => {
                        colorDictionary[workItemTypeName.toLowerCase()] = WorkItemTypeColorAndIconsProvider.getInstance().getColorAndIcon(
                            projectName,
                            workItemTypeName
                        );
                    });

                    return colorDictionary;
                });
        });
    }

    private static checkAndInsertWorkItemType(workItemTypes: string[], entry: ProcessesEntry): void {
        if (workItemTypes.indexOf(entry.WorkItemType) < 0) {
            workItemTypes.push(entry.WorkItemType);
        }
    }
}
